import { DOCUMENT, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { MetaDataService } from '../services/meta-data.service';
import { isPlatformBrowser } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class SeoTitleStrategy extends TitleStrategy {
  private readonly metaService = inject(MetaDataService);
  private readonly title = inject(Title);

  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const deepest = this.getDeepest(snapshot.root);
    const data = deepest.data;

    const variant = data['variant'];
    const seoData = data['seo'] && variant ? data['seo'][variant] : data;

    const pageTitle = seoData['meta_title'] ?? this.buildTitle(snapshot);
    if (pageTitle) {
      this.title.setTitle(pageTitle);

      if (isPlatformBrowser(this.platformId)) {
        setTimeout(() => {
          const titleTag = document.querySelector('title');
          if (titleTag) {
            const current = titleTag.textContent;
            titleTag.textContent = current;
          }
        }, 10);
      }
    }

    this.metaService.updateTags({
      title: seoData['meta_title'],
      description: seoData['meta_desc'],
      canonical: seoData['canonical_url'],
      robot_noindex: seoData['robot_noindex'],
      langCode: seoData['lang_code'],

      ogTitle: seoData['og_title'],
      ogDesc: seoData['og_desc'],
      ogUrl: seoData['og_url'],
      ogImage: seoData['og_image'],
      ogImageType: seoData['og_image_type'],
      ogImageAlt: seoData['og_image_alt'],
      ogLocale: seoData['og_locale'],
      twitterTitle: seoData['twitter_title'],
      twitterDesc: seoData['twitter_desc'],
      twitterImage: seoData['twitter_image'],
      twitterImageAlt: seoData['twitter_image_alt'],

      hrefLang: seoData['href_lang'],
      hrefLangs: seoData['href_langs'],
      jsonLd: seoData['json_ld'],
      ratingSchema: seoData['ratingSchema'],
      organizationSchema: seoData['organizationSchema'],
    });

    const breadcrumbs = this.buildBreadcrumbs(snapshot);
    this.metaService.updateBreadcrumbJsonLd(breadcrumbs);
  }

  private getDeepest(route: ActivatedRouteSnapshot) {
    while (route.firstChild) {
      route = route.firstChild;
    }
    return route;
  }

  private buildBreadcrumbs(snapshot: RouterStateSnapshot): { label: string; url: string }[] {
    const breadcrumbs: { label: string; url: string }[] = [];
    let route = snapshot.root;
    let url = '';

    breadcrumbs.push({ label: 'Home', url: 'https://infraon.io/' });

    while (route.firstChild) {
      route = route.firstChild;

      if (route.url.length) {
        const segment = route.url.map((s) => s.path).join('/');

        if (segment === 'prices') {
          url += '/' + segment;
          continue;
        }

        url += '/' + segment;
      }

      const label =
        route.data?.['breadcrumb'] || route.data?.['seo']?.[route.data?.['variant']]?.breadcrumb;

      if (label) {
        const currentUrl = `https://infraon.io${url}`;
        if (!breadcrumbs.find((b) => b.url === currentUrl)) {
          breadcrumbs.push({ label, url: currentUrl });
        }
      }
    }

    if (breadcrumbs.length >= 2) {
      const last = breadcrumbs[breadcrumbs.length - 1];
      const secondLast = breadcrumbs[breadcrumbs.length - 2];

      if (last.url === secondLast.url) {
        breadcrumbs.pop();
      }
    }

    return breadcrumbs;
  }

  private injectJsonLd(id: string, json: object) {
    let script = this.document.getElementById(id) as HTMLScriptElement;

    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      this.document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(json);
  }

  injectratingSchema(id: string, json: string) {
    let script = this.document.getElementById(id) as HTMLScriptElement;

    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      this.document.head.appendChild(script);
    }

    script.textContent = json;
  }

  injectOrganizationSchema(id: string, json: string) {
    let script = this.document.getElementById(id) as HTMLScriptElement;

    if (!script) {
      script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      this.document.head.appendChild(script);
    }

    script.textContent = json;
  }
}
